import { z } from 'zod';

// Schemas for core-one's cross-account monitoring endpoints, field by field from core-one fn-19's
// "API contract" section. Core One leaves null fields out of the JSON, so the fields the contract
// marks nullable are `.nullish()` (absent or null both parse).

const GroupCountSchema = z.looseObject({
	errors: z.number(),
	tables: z.number(),
});

/** Errors and tables per group: needs a person (`terminal`), `retrying`, `outdated`. */
export const GroupCountsSchema = z.looseObject({
	terminal: GroupCountSchema,
	retrying: GroupCountSchema,
	outdated: GroupCountSchema,
});
export type IGroupCounts = z.infer<typeof GroupCountsSchema>;

export const MonitoringEnvironmentSchema = z.looseObject({
	accountId: z.string(),
	environmentId: z.string(),
	environmentName: z.string(),
	databaseName: z.string(),
	groups: GroupCountsSchema,
	kinds: z.array(z.looseObject({ kind: z.string(), terminal: z.number(), retrying: z.number() })),
	tableScopeKinds: z.array(z.looseObject({ kind: z.string(), tables: z.number() })),
	lastFullPassAt: z.string().nullish(),
	/** The environment's audit status as One Monitor shows it (aligned, differing, ...), as of the last roll-up pass. */
	auditStatus: z.string().nullish(),
});
export type IMonitoringEnvironment = z.infer<typeof MonitoringEnvironmentSchema>;

/** GET /one/monitoring/overview: one resource, not paged. */
export const MonitoringOverviewSchema = z.looseObject({
	self: z.string(),
	intervalMinutes: z.number(),
	oldestPassStartedAt: z.string().nullish(),
	staleAccounts: z.array(z.looseObject({ accountId: z.string(), lastPassStartedAt: z.string().nullish() })),
	totals: GroupCountsSchema,
	environments: z.array(MonitoringEnvironmentSchema),
});
export type IMonitoringOverview = z.infer<typeof MonitoringOverviewSchema>;

/** One item of GET /one/monitoring/errors: one table's errors of one lifecycle generation and version. */
export const MonitoringErrorRowSchema = z.looseObject({
	self: z.string(),
	key: z.string(),
	accountId: z.string(),
	environmentId: z.string(),
	environmentName: z.string(),
	tableName: z.string(),
	databaseName: z.string(),
	lifecycleGeneration: z.number(),
	version: z.string().nullish(),
	environmentLifecycleGeneration: z.number(),
	environmentVersion: z.string().nullish(),
	outdated: z.boolean(),
	outdatedReason: z.string().nullish(),
	updatedAt: z.string(),
	/** The table's audit status as One Monitor shows it, as of the last roll-up pass. */
	auditStatus: z.string().nullish(),
	counts: z.array(
		z.looseObject({
			scope: z.string(),
			kind: z.string(),
			isRetriable: z.boolean(),
			isTerminal: z.boolean(),
			count: z.number(),
		}),
	),
});
export type IMonitoringErrorRow = z.infer<typeof MonitoringErrorRowSchema>;

/** The /errors filters the page sends; `kind` is repeated once per value. */
export interface IMonitoringErrorsParams {
	terminal?: boolean;
	includeOutdated?: boolean;
	kind?: readonly string[];
}
